import * as React from "react";
import {ChangeEventHandler, useCallback, useMemo} from "react";
import {useTranslation} from "react-i18next";
import {Box, SelectChangeEvent} from "@mui/material";
import dayjs from "dayjs";
import {OrderStatus} from "@/model/order";
import {OrderStatusBadge} from "@/common/order/OrderStatusBadge";
import MultiSelectInputField from "@/lib/components/inputfields/multi-select-input-field";
import DateInputField from "@/lib/components/inputfields/date-input-field";
import StringInputField from "@/lib/components/inputfields/string-input-field";
import SelectInputField from "@/lib/components/inputfields/select-input-field";
import {Constants} from "@/api/constants";
import {orderStatusOptions} from "@/datacaches";
import {CommonI18N, OrderI18N, OverviewTableI18N} from "@/translations";
import {OrderOverviewFilters} from "@/common/overview-state/order-view-state";
import {ORDER_TRACKING_COLUMN_IDS} from "@/common/overview-table/overview-table-column-ids";
import {ColumnFilterRow} from "@/common/overview-table/ColumnFilterRow";
import {isOperatorRole, useAuth} from "@/app/auth/AuthContext";

/** The raw select value <-> `boolean | undefined` conversion shared by every select on this page.
 *  Copied from `RequestColumnFilters.tsx` rather than exported across page modules (Phase 4.2 plan note). */
function fromTriStateSelectValue(raw: string): boolean | undefined {
    if (raw === "true") {
        return true;
    }
    if (raw === "false") {
        return false;
    }
    return undefined;
}

/** `undefined`/`true` <-> the two sentinel strings the two-option `hasUnreadMessages` select renders
 *  (#1836). Anything other than `true`, including a stale stored `false`, maps to "all" so the select
 *  never blanks. Copied from `RequestColumnFilters.tsx` for the same reason the tri-state helpers are. */
type UnreadMessagesSelectValue = "all" | "true";

function toUnreadMessagesSelectValue(value: boolean | undefined): UnreadMessagesSelectValue {
    return value === true ? "true" : "all";
}

export interface OrderColumnFilterFieldsProps {
    /** The applied filters (Sofortwirkung) — read-only here, every change goes back through `onChange`. */
    filters: OrderOverviewFilters;
    /** Wired to `setFilters` by the caller — never writes onto the applied state directly. */
    onChange: (partial: Partial<OrderOverviewFilters>) => void;
}

/** Curried text-field handler for the three column text filters (`orderNumberContains`,
 *  `quoteNumberContains`, `deliveryAddressContains`), shaped like the request side's own
 *  `useTextFieldChange`. */
function useTextFieldChange(
    field: keyof OrderOverviewFilters,
    onChange: (partial: Partial<OrderOverviewFilters>) => void
): ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement> {
    return useCallback(
        (event) =>
            onChange({
                [field]: event.target.value === "" ? undefined : event.target.value
            } as Partial<OrderOverviewFilters>),
        [field, onChange]
    );
}

/** Curried tri-state handler for `hasUnreadMessages`, shaped like the request side's own
 *  `useTriStateFieldChange`. */
function useTriStateFieldChange(
    field: keyof OrderOverviewFilters,
    onChange: (partial: Partial<OrderOverviewFilters>) => void
): ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> {
    return useCallback(
        (event) =>
            onChange({[field]: fromTriStateSelectValue(event.target.value)} as Partial<OrderOverviewFilters>),
        [field, onChange]
    );
}

/** The "Auftragsnummer" column's filter content: a contains-search text field on `orderNumberContains`. */
const OrderNumberColumnFilter: React.FC<OrderColumnFilterFieldsProps> = ({filters, onChange}) => {
    const {t} = useTranslation();
    const handleChange = useTextFieldChange("orderNumberContains", onChange);

    return (
        <Box sx={{minWidth: 220}}>
            <StringInputField
                name="orderNumberContains"
                label={t(OrderI18N.table.column.orderNumber)}
                value={filters.orderNumberContains ?? ""}
                onChange={handleChange}
                maxChars={Constants.ORDER_SEARCH_TEXT_LENGTH_MAX}
            />
        </Box>
    );
};

/** The "Angebotsnummer" column's filter content: a contains-search text field on `quoteNumberContains`. */
const QuoteNumberColumnFilter: React.FC<OrderColumnFilterFieldsProps> = ({filters, onChange}) => {
    const {t} = useTranslation();
    const handleChange = useTextFieldChange("quoteNumberContains", onChange);

    return (
        <Box sx={{minWidth: 220}}>
            <StringInputField
                name="quoteNumberContains"
                label={t(OrderI18N.table.column.quoteNumber)}
                value={filters.quoteNumberContains ?? ""}
                onChange={handleChange}
                maxChars={Constants.ORDER_SEARCH_TEXT_LENGTH_MAX}
            />
        </Box>
    );
};

/** The "Lieferadresse" column's filter content: a contains-search text field on `deliveryAddressContains`. */
const DeliveryAddressColumnFilter: React.FC<OrderColumnFilterFieldsProps> = ({filters, onChange}) => {
    const {t} = useTranslation();
    const handleChange = useTextFieldChange("deliveryAddressContains", onChange);

    return (
        <Box sx={{minWidth: 220}}>
            <StringInputField
                name="deliveryAddressContains"
                label={t(OrderI18N.table.column.deliveryAddress)}
                value={filters.deliveryAddressContains ?? ""}
                onChange={handleChange}
                maxChars={Constants.ORDER_SEARCH_TEXT_LENGTH_MAX}
            />
        </Box>
    );
};

/** The status column's filter content: a multi-select of every `OrderStatus`. */
const OrderStatusColumnFilter: React.FC<OrderColumnFilterFieldsProps> = ({filters, onChange}) => {
    const {t} = useTranslation();

    const statusOptions = useMemo(
        () =>
            orderStatusOptions.map((s) => ({
                label: t(CommonI18N.enum.orderStatus[s as keyof typeof CommonI18N.enum.orderStatus] ?? s),
                value: s
            })),
        [t]
    );

    const handleStatusesChange = useCallback(
        (event: SelectChangeEvent<unknown[]>) => {
            const raw = event.target.value;
            const next = (Array.isArray(raw) ? raw : []).filter((v): v is OrderStatus =>
                orderStatusOptions.includes(v as OrderStatus)
            );
            onChange({orderStatuses: next});
        },
        [onChange]
    );

    return (
        <Box sx={{minWidth: 220}}>
            <MultiSelectInputField
                name="orderStatuses"
                label={t(OrderI18N.table.column.status)}
                value={filters.orderStatuses ?? []}
                onChange={handleStatusesChange}
                options={statusOptions}
                renderOption={(option) => <OrderStatusBadge key={option.value} status={option.value} />}
            />
        </Box>
    );
};

/** The "Geplanter Lieferzeitraum" column's filter content: "von" and "bis" side by side (Standing constraint 9). */
const OrderPlannedDeliveryColumnFilter: React.FC<OrderColumnFilterFieldsProps> = ({filters, onChange}) => {
    const {t} = useTranslation();
    const fromLabel = `${t(OrderI18N.table.column.plannedDeliveryDates)} (${t(OverviewTableI18N.field.rangeFrom)})`;
    const toLabel = `${t(OrderI18N.table.column.plannedDeliveryDates)} (${t(OverviewTableI18N.field.rangeTo)})`;

    const handleFromChange = useCallback(
        ({value}: {name: string; value: unknown}) =>
            onChange({plannedDeliveryFrom: value instanceof Date ? dayjs(value).format("YYYY-MM-DD") : undefined}),
        [onChange]
    );
    const handleToChange = useCallback(
        ({value}: {name: string; value: unknown}) =>
            onChange({plannedDeliveryTo: value instanceof Date ? dayjs(value).format("YYYY-MM-DD") : undefined}),
        [onChange]
    );

    return (
        <ColumnFilterRow fields={[{label: fromLabel}, {label: toLabel}]}>
            <DateInputField
                name="plannedDeliveryFrom"
                label={fromLabel}
                value={filters.plannedDeliveryFrom ?? null}
                onChange={handleFromChange}
            />
            <DateInputField
                name="plannedDeliveryTo"
                label={toLabel}
                value={filters.plannedDeliveryTo ?? null}
                onChange={handleToChange}
            />
        </ColumnFilterRow>
    );
};

/** The "Bestätigter Liefertermin" column's filter content: "von" and "bis" side by side (Standing constraint 9). */
const OrderConfirmedDeliveryColumnFilter: React.FC<OrderColumnFilterFieldsProps> = ({filters, onChange}) => {
    const {t} = useTranslation();
    const fromLabel = `${t(OrderI18N.table.column.confirmedDeliveryDate)} (${t(OverviewTableI18N.field.rangeFrom)})`;
    const toLabel = `${t(OrderI18N.table.column.confirmedDeliveryDate)} (${t(OverviewTableI18N.field.rangeTo)})`;

    const handleFromChange = useCallback(
        ({value}: {name: string; value: unknown}) =>
            onChange({
                confirmedDeliveryDateFrom: value instanceof Date ? dayjs(value).format("YYYY-MM-DD") : undefined
            }),
        [onChange]
    );
    const handleToChange = useCallback(
        ({value}: {name: string; value: unknown}) =>
            onChange({
                confirmedDeliveryDateTo: value instanceof Date ? dayjs(value).format("YYYY-MM-DD") : undefined
            }),
        [onChange]
    );

    return (
        <ColumnFilterRow fields={[{label: fromLabel}, {label: toLabel}]}>
            <DateInputField
                name="confirmedDeliveryDateFrom"
                label={fromLabel}
                value={filters.confirmedDeliveryDateFrom ?? null}
                onChange={handleFromChange}
            />
            <DateInputField
                name="confirmedDeliveryDateTo"
                label={toLabel}
                value={filters.confirmedDeliveryDateTo ?? null}
                onChange={handleToChange}
            />
        </ColumnFilterRow>
    );
};

/**
 * The "Info" column's filter content: the two-option `hasUnreadMessages` select, same label key the
 * request side's Info column uses (Phase 4.2 plan note). Since #1836 it offers only "Alle" and "Nur
 * ungelesene Nachrichten", no explicit "Nein" any more; a stale stored `false` renders as "Alle" via
 * `toUnreadMessagesSelectValue`. Hidden for an Innendienst-Mitarbeiter (constraint 7): that role never
 * receives an unread count above 0, so offering the control would only ever narrow to an empty result.
 * `renderOrderColumnFilter` is only reached for `INTERNAL`/`EXTERNAL` (`buildInfoColumn` is not rendered
 * for `NEUTRAL`), so no variant check is needed here.
 */
const OrderInfoColumnFilter: React.FC<OrderColumnFilterFieldsProps> = ({filters, onChange}) => {
    const {t} = useTranslation();
    const {roles} = useAuth();
    const isOperator = isOperatorRole(roles);
    const handleHasUnreadMessagesChange = useTriStateFieldChange("hasUnreadMessages", onChange);

    const unreadMessagesOptions = useMemo(
        () => [
            {label: t(OverviewTableI18N.field.triStateAll), value: "all"},
            {label: t(OverviewTableI18N.field.onlyUnreadMessages), value: "true"}
        ],
        [t]
    );

    if (isOperator) {
        return null;
    }

    return (
        <Box sx={{minWidth: 220}}>
            <SelectInputField
                name="hasUnreadMessages"
                label={t(OverviewTableI18N.field.hasUnreadMessages)}
                value={toUnreadMessagesSelectValue(filters.hasUnreadMessages)}
                onChange={handleHasUnreadMessagesChange}
                options={unreadMessagesOptions}
            />
        </Box>
    );
};

/**
 * Renders one order column's filter content for `DataTable`'s `renderColumnFilter` (plan
 * "Spaltenkopf-Filterung und Sortieranzeige", Phase 2.3, Standing constraint 2) — looked up by
 * `columnId` (`meta.filterColumnId`), replacing `OrderFilterSheetFields.tsx`'s single vertical field
 * stack with per-column content. `undefined` for any column id not carrying a filter (`DataTable`
 * only calls this for columns that set `meta.filterColumnId`, so this case is never actually hit).
 */
export function renderOrderColumnFilter(
    columnId: string,
    filters: OrderOverviewFilters,
    onChange: (partial: Partial<OrderOverviewFilters>) => void
): React.ReactNode | undefined {
    switch (columnId) {
        case ORDER_TRACKING_COLUMN_IDS.status:
            return <OrderStatusColumnFilter filters={filters} onChange={onChange} />;
        case ORDER_TRACKING_COLUMN_IDS.plannedDeliveryDates:
            return <OrderPlannedDeliveryColumnFilter filters={filters} onChange={onChange} />;
        case ORDER_TRACKING_COLUMN_IDS.deliveryDate:
            return <OrderConfirmedDeliveryColumnFilter filters={filters} onChange={onChange} />;
        case ORDER_TRACKING_COLUMN_IDS.orderNumber:
            return <OrderNumberColumnFilter filters={filters} onChange={onChange} />;
        case ORDER_TRACKING_COLUMN_IDS.quoteNumber:
            return <QuoteNumberColumnFilter filters={filters} onChange={onChange} />;
        case ORDER_TRACKING_COLUMN_IDS.deliveryAddress:
            return <DeliveryAddressColumnFilter filters={filters} onChange={onChange} />;
        case ORDER_TRACKING_COLUMN_IDS.info:
            return <OrderInfoColumnFilter filters={filters} onChange={onChange} />;
        default:
            return undefined;
    }
}
